import { useId, useState } from "react";
import {
  CheckCircle2,
  CircleSlash2,
  LoaderCircle,
  MessageSquareMore,
  UsersRound,
  XCircle,
} from "lucide-react";
import type { QueueBand } from "../types";
import "./ArrivalCheckIn.css";

export type ArrivalCheckInReport =
  | { status: "available" }
  | { status: "queue"; queueBand: QueueBand }
  | { status: "unavailable" };

export type ArrivalCheckInProps = {
  facilityName: string;
  onSubmit: (report: ArrivalCheckInReport) => Promise<unknown>;
  onDismiss: () => void;
  onOpenFullReport?: () => void;
  fallbackName?: string;
  onNavigateFallback?: () => void;
};

type QuickStatus = ArrivalCheckInReport["status"];
type SubmissionPhase = "idle" | "submitting" | "success" | "error";

const quickChoices = [
  {
    status: "available",
    label: "使えた",
    icon: CheckCircle2,
  },
  {
    status: "queue",
    label: "待った",
    icon: UsersRound,
  },
  {
    status: "unavailable",
    label: "使えなかった",
    icon: XCircle,
  },
] as const;

const queueChoices: Array<{ value: QueueBand; label: string }> = [
  { value: "under_5_min", label: "5分未満" },
  { value: "5_to_15_min", label: "5〜15分" },
  { value: "over_15_min", label: "15分以上" },
];

export function ArrivalCheckIn({
  facilityName,
  onSubmit,
  onDismiss,
  onOpenFullReport,
  fallbackName,
  onNavigateFallback,
}: ArrivalCheckInProps) {
  const titleId = useId();
  const [phase, setPhase] = useState<SubmissionPhase>("idle");
  const [activeStatus, setActiveStatus] = useState<QuickStatus>();

  const submit = async (report: ArrivalCheckInReport) => {
    if (phase === "submitting") return;

    setPhase("submitting");
    setActiveStatus(report.status);
    try {
      await onSubmit(report);
      setPhase("success");
    } catch {
      setPhase("error");
      setActiveStatus(undefined);
    }
  };

  const submitting = phase === "submitting";

  return (
    <section
      className="arrival-check-in"
      aria-labelledby={titleId}
      aria-busy={submitting}
    >
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        到着後の確認。{facilityName}を利用できましたか？
      </p>
      <div className="arrival-check-in__heading">
        <div>
          <p className="arrival-check-in__eyebrow">到着後の確認</p>
          <h2 id={titleId}>{facilityName}</h2>
        </div>
      </div>

      {phase === "success" ? (
        <div className="arrival-check-in__success" role="status" aria-live="polite">
          <CheckCircle2 aria-hidden="true" />
          <div>
            <strong>確認を反映しました</strong>
            <span>ありがとうございます</span>
          </div>
          <div className="arrival-check-in__success-actions">
            {activeStatus === "unavailable" && onNavigateFallback ? (
              <button
                className="arrival-check-in__fallback"
                type="button"
                onClick={onNavigateFallback}
                aria-label={
                  fallbackName
                    ? `${fallbackName}への案内を開始`
                    : "次の候補への案内を開始"
                }
              >
                次候補へ案内
              </button>
            ) : null}
            <button type="button" onClick={onDismiss}>
              閉じる
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="arrival-check-in__prompt">利用できましたか？</p>
          <div className="arrival-check-in__choices">
            {quickChoices.map(({ status, label, icon: Icon }) => (
              <button
                className={`arrival-check-in__choice arrival-check-in__choice--${status}`}
                type="button"
                key={status}
                onClick={() => {
                  if (status === "queue") {
                    setActiveStatus("queue");
                    return;
                  }
                  void submit({ status });
                }}
                disabled={submitting}
                data-active={activeStatus === status || undefined}
              >
                {submitting && activeStatus === status ? (
                  <LoaderCircle className="arrival-check-in__spinner" aria-hidden="true" />
                ) : (
                  <Icon aria-hidden="true" />
                )}
                <span>{label}</span>
              </button>
            ))}
            <button
              className="arrival-check-in__choice arrival-check-in__choice--unused"
              type="button"
              onClick={onDismiss}
              disabled={submitting}
            >
              <CircleSlash2 aria-hidden="true" />
              <span>利用しなかった</span>
            </button>
          </div>

          {activeStatus === "queue" && !submitting ? (
            <div className="arrival-check-in__queue" role="group" aria-label="待ち時間">
              <span>待ち時間</span>
              <div>
                {queueChoices.map((choice) => (
                  <button
                    type="button"
                    key={choice.value}
                    onClick={() =>
                      void submit({ status: "queue", queueBand: choice.value })
                    }
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="arrival-check-in__feedback" aria-live="polite">
            {submitting ? (
              <p role="status">送信中…</p>
            ) : phase === "error" ? (
              <p role="alert">送信できませんでした。もう一度お試しください。</p>
            ) : null}
          </div>

          {onOpenFullReport ? (
            <button
              className="arrival-check-in__full-report"
              type="button"
              onClick={onOpenFullReport}
              disabled={submitting}
            >
              <MessageSquareMore aria-hidden="true" />
              詳しく報告
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
